import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import './SsnModule.css';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import { useAcquisitionContext } from '../../providers/AcquisitionProvider/AcquisitionProvider';
import { Row, Col, Title, Section } from '../../importCL';
import SsnField from '../FieldContainers/SsnField/SsnField';
import AcquisitionErrorToggleEvent from '../ApplicationCta/AcquisitionErrorToggleEvent';
import { generateFieldErrorMessage } from '../../utils/error-message';

interface ISsnModuleProps extends MappedComponentProperties {
    label?: string;
    message?: string;
    subtitle?: string;
    title?: string;
    topBorderEnabled?: boolean;
    bottomBorderEnabled?: boolean;
}

export const SsnModuleEditConfig = {
    emptyLabel: 'Ssn Module',
    isEmpty: (props: PropsWithChildren<ISsnModuleProps>) => {
        return props.title || props.label;
    },
};

const SsnModule = (props: PropsWithChildren<ISsnModuleProps>): JSX.Element => {
    const ssnRef = useRef<HTMLInputElement>();
    const [isBlurred, setIsBlurred] = useState(false);
    const {
        ssnInfo,
        setSsnInfo,
        isSsnInfoValid,
        setPersonalInfoForm,
        setIsSsnInfoValid,
    } = useAcquisitionContext();

    useEffect(
        () =>
            setPersonalInfoForm(prev => ({
                ...prev,
                ssn: { valid: isSsnInfoValid, value: ssnInfo },
            })),
        [ssnInfo, isSsnInfoValid, setPersonalInfoForm],
    );

    useEffect(() => {
        // TODO: When ssnField is refactor to use Function Pipe, pass through the validating field value there instead
        const errorListener = (info: AcquisitionErrorToggleEvent) => {
            setIsBlurred(true);
            setIsSsnInfoValid(
                info?.personalInformation?.ssn?.value.length === 9,
            );
        };

        window.addEventListener(
            AcquisitionErrorToggleEvent.EventName,
            errorListener,
        );
        return () => {
            window.removeEventListener(
                AcquisitionErrorToggleEvent.EventName,
                errorListener,
            );
        };
    }, [setIsBlurred, setIsSsnInfoValid]);

    return (
        <>
            <Section className='SsnModule'>
                {props.topBorderEnabled && <hr role='separator' />}
                <Row className='px-4'>
                    <Col>
                        <Title tag='h3' className='SsnModule__title'>
                            {props.title}
                        </Title>
                        <div className='SsnModule__subtitle'>
                            {transformHTMLParserLinks(props.subtitle ?? '')}
                        </div>
                    </Col>
                </Row>
                <Row className='px-4'>
                    <Col md={4} id='socialSecurity-field-container'>
                        <form autoComplete='off' className='SsnModule__form' onSubmit={(e) => e.preventDefault()}>
                            <SsnField
                                required
                                className='SsnModule_input'
                                ref={ssnRef}
                                id='Ssn'
                                name='ssn'
                                inputClass='PersonalInformation__input'
                                value={ssnInfo}
                                setValueCallback={setSsnInfo}
                                label={props.label}
                                onBlurCallback={() => setIsBlurred(true)}
                                onFocusCallback={() => setIsBlurred(false)}
                                valid={[isSsnInfoValid, setIsSsnInfoValid]}
                                requiredMessage={generateFieldErrorMessage('Social Security Number', 'missingValue')}
                                errorMessage={generateFieldErrorMessage('Social Security Number')}
                                invalidErrorMessage={generateFieldErrorMessage('Social Security Number')}
                                error={isBlurred && !isSsnInfoValid}
                                onPaste={(e: any) => {
                                    e.preventDefault();
                                }}
                                disclaimerMessage={props.message}
                            />
                        </form>
                    </Col>
                </Row>
                {props.bottomBorderEnabled && <hr role='separator' />}
            </Section>
        </>
    );
};
export default SsnModule;
